import React, { useContext, useCallback, useEffect, useRef } from 'react';
import { compose } from 'recompose';
import { googleMapsEnhance } from 'modules/google-maps/utils';
import { withTheme } from 'styled-components';
import { GoogleMap } from 'react-google-maps';
import ReactTooltip from 'react-tooltip';

import { getBoundsWithStops, getCenterAndNorthEast } from './utils';

import mapOptions from './options';

import AssignmentMapContext from 'modules/assignment-map/map/context';

import JourneyStopsDrawer from './JourneyStopsDrawer';
import DriversPositionsDrawer from './DriversPositionsDrawer';
import DriverCard from './DriverCard';
import Loaders from './Loaders';

const enhance = compose(withTheme, googleMapsEnhance);

const euopeCenter = { lat: 54.526, lng: 15.2551 };

const MapEnhanced = enhance(() => {
  const {
    itinerary,
    itineraryId,
    fetchDriverPositions,
    clearItinerary,
    clearPositions,
    fetchItinerary,
  } = useContext(AssignmentMapContext);

  const { stops } = itinerary || {};

  const mapRef = useRef(null);

  const fetchPositions = useCallback(
    (stops) => {
      if (!stops) {
        return;
      }

      const bounds = mapRef.current.getBounds();
      const positions = getCenterAndNorthEast(bounds);

      fetchDriverPositions(positions);
    },
    [fetchDriverPositions]
  );

  const handleTilesLoaded = useCallback(() => {
    setTimeout(() => ReactTooltip.rebuild(), 200);
  }, []);

  const handleRefreshPositions = useCallback(() => {
    fetchPositions(stops);
  }, [fetchPositions, stops]);

  useEffect(() => {
    fetchPositions(stops);
    return () => {
      clearPositions();
    };
  }, [fetchPositions, clearPositions, stops]);

  useEffect(() => {
    fetchItinerary(itineraryId);
    return () => {
      clearItinerary();
    };
  }, [fetchItinerary, clearItinerary, itineraryId]);

  useEffect(() => {
    if (!stops) {
      return;
    }
    const bounds = getBoundsWithStops(stops);
    mapRef.current.fitBounds(bounds);
  }, [stops]);

  return (
    <GoogleMap
      ref={mapRef}
      defaultZoom={4}
      defaultCenter={euopeCenter}
      defaultOptions={mapOptions}
      onDragEnd={handleRefreshPositions}
      onZoomChanged={handleRefreshPositions}
      onTilesLoaded={handleTilesLoaded}
    >
      <JourneyStopsDrawer />
      <DriversPositionsDrawer />
      <DriverCard />
      <Loaders />
    </GoogleMap>
  );
});

const AssignmentMap = ({ className }) => {
  return (
    <div className={className} data-testid="AssignmentMap">
      <MapEnhanced />
    </div>
  );
};

export default AssignmentMap;
